import React, { useEffect, useState } from "react";

import { useLingui } from "@lingui/react";

import { SxProps, Theme } from "@mui/material";
import Snackbar, { SnackbarProps } from "@mui/material/Snackbar";

import Toast from "@readai/common/components/Toast";
import useToasts from "@readai/common/hooks/useToasts";
import { isMessageDescriptor } from "@readai/common/utils";

interface ToastsProps {
  sx?: SxProps<Theme>;
}

const Toasts = ({ sx }: ToastsProps) => {
  const { toast, hideToast } = useToasts();

  const [open, setOpen] = useState(false);

  const { _ } = useLingui();

  useEffect(() => setOpen(Boolean(toast)), [toast]);

  const handleClickClose = () => setOpen(false);

  const handleClose: SnackbarProps["onClose"] = (_event, reason) => {
    if (reason === "clickaway" && toast?.disableClickAway) {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => hideToast();

  const title =
    toast?.type === "system" && toast?.title
      ? typeof toast.title === "string"
        ? toast.title
        : _(toast.title)
      : undefined;

  const message = toast?.message
    ? isMessageDescriptor(toast.message)
      ? _(toast.message)
      : toast.message
    : undefined;

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      autoHideDuration={toast?.sticky ? undefined : 4000}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{
        vertical: toast?.type === "system" ? "bottom" : "top",
        horizontal: toast?.type === "system" ? "right" : "center",
        ...toast?.anchorOrigin,
      }}
      sx={sx}
    >
      {toast && (
        <Toast
          severity={toast.severity}
          onClick={toast.dismissible ? handleClickClose : undefined}
          onClose={toast.dismissible ? handleClickClose : undefined}
          type={toast.type ?? "default"}
          title={title}
          action={toast.type === "system" ? toast.action : undefined}
        >
          {message}
        </Toast>
      )}
    </Snackbar>
  );
};

export default Toasts;
